import { makeAutoObservable, runInAction } from "mobx";
import apiHandler from "../handlers/ApiHandler";

class RoleStore {
  roles = [];
  rolePermissions = {};
  availablePermissions = [];
  isLoading = false;
  error = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchRoles() {
    this.isLoading = true;
    this.error = null;
    try {
      const response = await apiHandler.get("/api/roles");
      runInAction(() => {
        this.roles = response;
      });

      // Fetch assigned permissions for each role
      response.forEach((role) => this.fetchRolePermissions(role.id));
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error("Error fetching roles:", error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchRolePermissions(roleId) {
    try {
      const response = await apiHandler.get(`/api/roles/${roleId}/permissions`);
      runInAction(() => {
        this.rolePermissions[roleId] = response;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error(`Error fetching permissions for role ${roleId}:`, error);
    }
  }

  async fetchAvailablePermissions() {
    try {
      const response = await apiHandler.get("/api/permissions");
      runInAction(() => {
        this.availablePermissions = response;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error("Error fetching available permissions:", error);
    }
  }

  async createRole(newRole) {
    try {
      const response = await apiHandler.post("/api/roles", newRole);
      runInAction(() => {
        this.roles.push(response);
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error("Error creating role:", error);
    }
  }

  async updateRole(roleId, roleData) {
    try {
      const response = await apiHandler.put(`/api/roles/${roleId}`, roleData);
      runInAction(() => {
        const index = this.roles.findIndex((role) => role.id === roleId);
        if (index !== -1) {
          this.roles[index] = response;
        }
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error(`Error updating role with ID ${roleId}:`, error);
    }
  }

  async deleteRole(roleId) {
    try {
      await apiHandler.delete(`/api/roles/${roleId}`);
      runInAction(() => {
        this.roles = this.roles.filter((role) => role.id !== roleId);
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error(`Error deleting role with ID ${roleId}:`, error);
    }
  }

  async assignPermission(roleId, permissionId) {
    try {
      await apiHandler.post(`/api/roles/${roleId}/permissions/${permissionId}`);
      console.log(`Permission ${permissionId} assigned to role ${roleId}`);
      this.fetchRolePermissions(roleId); // Refresh the assigned permissions
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error("Error assigning permission:", error);
    }
  }

  async removePermission(roleId, permissionId) {
    try {
      await apiHandler.delete(
        `/api/roles/${roleId}/permissions/${permissionId}`
      );
      console.log(`Permission ${permissionId} removed from role ${roleId}`);
      this.fetchRolePermissions(roleId); // Refresh the assigned permissions
    } catch (error) {
      runInAction(() => {
        this.error = error.toString();
      });
      console.error("Error removing permission:", error);
    }
  }

  clearError() {
    this.error = null;
  }
}

const roleStore = new RoleStore();
export default roleStore;
